import {
  DownOutlined,
  ExportOutlined,
  FileTextOutlined,
  GlobalOutlined,
  SettingOutlined,
  UploadOutlined,
  AppstoreOutlined,
  NodeIndexOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  UnorderedListOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import { Skeleton, Tooltip } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MODULES,
  PERMISSION_FEATURES,
} from "../../permissions/types/permission-types";
import { useAuth, useAuthContext } from "../contexts/AuthContextProvider";
import { ROUTES } from "../navigation";

const useStyles = createUseStyles({
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 0",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: 4 },
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 12px",
    margin: "1px 8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    color: "rgba(255,255,255,0.58)",
    borderRadius: "8px",
    transition: "all 0.18s ease",
    userSelect: "none",
    "&:hover": {
      color: "rgba(255,255,255,0.92)",
      background: "rgba(255,255,255,0.07)",
    },
  },
  navItemActive: {
    color: "#ffffff !important",
    background: "rgba(0,169,224,0.24) !important",
    fontWeight: "600 !important",
  },
  navIcon: {
    fontSize: "15px",
    width: "17px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navLabel: {
    flex: 1,
    lineHeight: 1.4,
  },
  parentItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 12px",
    margin: "1px 8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    color: "rgba(255,255,255,0.58)",
    borderRadius: "8px",
    transition: "all 0.18s ease",
    userSelect: "none",
    "&:hover": {
      color: "rgba(255,255,255,0.92)",
      background: "rgba(255,255,255,0.07)",
    },
  },
  parentOpen: {
    color: "#66CFF0 !important",
  },
  chevron: {
    marginLeft: "auto",
    fontSize: "11px",
    transition: "transform 0.25s ease",
    display: "inline-flex",
    opacity: 0.5,
    transform: "rotate(-90deg)",
  },
  chevronOpen: {
    transform: "rotate(0deg)",
    opacity: 0.9,
  },
  subItems: {
    overflow: "hidden",
    transition: "max-height 0.25s ease",
    maxHeight: 0,
    marginLeft: 28,
  },
  subItemsOpen: {
    maxHeight: "360px",
    borderLeft: "1.5px solid rgba(255,255,255,0.1)",
    paddingTop: 2,
    paddingBottom: 4,
  },
  subItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "7px 10px 7px 12px",
    margin: "1px 8px 1px 8px",
    cursor: "pointer",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.42)",
    borderRadius: "8px",
    transition: "all 0.18s ease",
    userSelect: "none",
    "&:hover": {
      color: "rgba(255,255,255,0.85)",
      background: "rgba(255,255,255,0.07)",
    },
  },
  subItemActive: {
    color: "#ffffff !important",
    background: "rgba(0,169,224,0.24) !important",
    fontWeight: "700 !important",
    borderRadius: "8px !important",
  },
  divider: {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "8px 16px",
  },
  bottomNav: {
    padding: "8px 0 16px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  menuLoad: {
    padding: "12px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  menuSkel: {
    width: "100% !important",
    minWidth: "unset !important",
    borderRadius: "6px !important",
    height: "36px !important",
  },
  externalIcon: {
    fontSize: "11px",
    opacity: 0.5,
    marginLeft: "auto",
  },
  navItemCollapsed: {
    justifyContent: "center",
    padding: "11px 0",
    margin: "1px 8px",
  },
});

const NavMenu = ({ collapsed }) => {
  const classes = useStyles();
  const [currentMenu, setCurrentMenu] = useState(ROUTES.dashboard);
  const [allRequestsOpen, setAllRequestsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const prevPathRef = useRef(null);

  const { isCustomer } = useAuth();
  const { isAuthorizedToAccess, isPermissionLoading, allPermissions } = useAuthContext();
  const navigate = useNavigate();
  const currentLoc = useLocation();

  useEffect(() => {
    if (!currentLoc.pathname || currentLoc.pathname === prevPathRef.current) return;
    prevPathRef.current = currentLoc.pathname;
    setCurrentMenu(currentLoc.pathname);
    const isSubRoute = currentLoc.pathname === ROUTES.edo || currentLoc.pathname === ROUTES.invoiceRequests;
    setAllRequestsOpen(isSubRoute);
    const isSettingsSubRoute =
      currentLoc.pathname === ROUTES.accessManagement ||
      currentLoc.pathname === ROUTES.approvals ||
      currentLoc.pathname === ROUTES.terminalManagement ||
      currentLoc.pathname === ROUTES.salesNomination;
    setSettingsOpen(isSettingsSubRoute);
  }, [currentLoc.pathname]);

  useEffect(() => {
    if (!isPermissionLoading) {
      if (
        !currentLoc.pathname?.split("/")[1] &&
        isAuthorizedToAccess(MODULES.DASHBOARD, PERMISSION_FEATURES.ENABLE) &&
        Object.values(allPermissions).some((arr) => arr.length > 0)
      ) {
        navigate(ROUTES.dashboard);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermissions, currentLoc.pathname, isPermissionLoading]);

  const go = useCallback((route) => navigate(route), [navigate]);

  const isActive = (route) => currentMenu === route;

  const tip = (label, el) =>
    collapsed ? <Tooltip key={label} title={label} placement="right">{el}</Tooltip> : el;

  const itemCls = (route) =>
    `${classes.navItem}${isActive(route) ? ` ${classes.navItemActive}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`;

  const subItemCls = (route) =>
    `${classes.subItem}${isActive(route) ? ` ${classes.subItemActive}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`;

  if (isPermissionLoading) {
    return (
      <div className={classes.menuLoad}>
        <Skeleton.Input active className={classes.menuSkel} block />
        <Skeleton.Input active className={classes.menuSkel} block />
        <Skeleton.Input active className={classes.menuSkel} block />
        <Skeleton.Input active className={classes.menuSkel} block />
      </div>
    );
  }

  if (isCustomer) {
    return (
      <>
        <nav className={classes.nav}>
          {tip("Dashboard",
            <div className={itemCls(ROUTES.dashboard)} onClick={() => go(ROUTES.dashboard)}>
              <span className={classes.navIcon}><AppstoreOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>Dashboard</span>}
            </div>
          )}

          {tip("All Requests",
            <div
              className={`${classes.parentItem}${allRequestsOpen ? ` ${classes.parentOpen}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`}
              onClick={() => setAllRequestsOpen((v) => !v)}
            >
              <span className={classes.navIcon}><FileTextOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>All Requests</span>}
              {!collapsed && <span className={`${classes.chevron}${allRequestsOpen ? ` ${classes.chevronOpen}` : ""}`}><DownOutlined /></span>}
            </div>
          )}
          {!collapsed && (
            <div className={`${classes.subItems}${allRequestsOpen ? ` ${classes.subItemsOpen}` : ""}`}>
              <div className={subItemCls(ROUTES.edo)} onClick={() => go(ROUTES.edo)}>
                <span className={classes.navIcon}><NodeIndexOutlined /></span>
                EDO Request
              </div>
              <div className={subItemCls(ROUTES.invoiceRequests)} onClick={() => go(ROUTES.invoiceRequests)}>
                <span className={classes.navIcon}><FileDoneOutlined /></span>
                Invoice Request
              </div>
            </div>
          )}
          {collapsed && (
            <>
              {tip("EDO Request",
                <div className={subItemCls(ROUTES.edo)} onClick={() => go(ROUTES.edo)}>
                  <span className={classes.navIcon}><NodeIndexOutlined /></span>
                </div>
              )}
              {tip("Invoice Request",
                <div className={subItemCls(ROUTES.invoiceRequests)} onClick={() => go(ROUTES.invoiceRequests)}>
                  <span className={classes.navIcon}><FileDoneOutlined /></span>
                </div>
              )}
            </>
          )}

          {tip("Bank Slip Re-upload",
            <div className={itemCls(ROUTES.creditBankSlipReUpload)} onClick={() => go(ROUTES.creditBankSlipReUpload)}>
              <span className={classes.navIcon}><UploadOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>Bank Slip Re-upload</span>}
            </div>
          )}

          {tip("Gensoft Track & Trace",
            <div className={itemCls(ROUTES.gensoftTrackTrace)} onClick={() => go(ROUTES.gensoftTrackTrace)}>
              <span className={classes.navIcon}><NodeIndexOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>Gensoft Track & Trace</span>}
            </div>
          )}

        </nav>
      </>
    );
  }

  return (
    <>
      <nav className={classes.nav}>
        {tip("Dashboard",
          <div className={itemCls(ROUTES.dashboard)} onClick={() => go(ROUTES.dashboard)}>
            <span className={classes.navIcon}><AppstoreOutlined /></span>
            {!collapsed && <span className={classes.navLabel}>Dashboard</span>}
          </div>
        )}

        {tip("All Requests",
          <div
            className={`${classes.parentItem}${allRequestsOpen ? ` ${classes.parentOpen}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`}
            onClick={() => setAllRequestsOpen((v) => !v)}
          >
            <span className={classes.navIcon}><FileTextOutlined /></span>
            {!collapsed && <span className={classes.navLabel}>All Requests</span>}
            {!collapsed && <span className={`${classes.chevron}${allRequestsOpen ? ` ${classes.chevronOpen}` : ""}`}><DownOutlined /></span>}
          </div>
        )}
        {!collapsed && (
          <div className={`${classes.subItems}${allRequestsOpen ? ` ${classes.subItemsOpen}` : ""}`}>
            {!isAuthorizedToAccess(MODULES.EDO, PERMISSION_FEATURES.VIEW) ? null : (
              <div className={subItemCls(ROUTES.edo)} onClick={() => go(ROUTES.edo)}>
                <span className={classes.navIcon}><NodeIndexOutlined /></span>
                EDO Request
              </div>
            )}
            {!isAuthorizedToAccess(MODULES.EDO, PERMISSION_FEATURES.VIEW) ? null : (
              <div className={subItemCls(ROUTES.invoiceRequests)} onClick={() => go(ROUTES.invoiceRequests)}>
                <span className={classes.navIcon}><FileDoneOutlined /></span>
                Invoice Request
              </div>
            )}
          </div>
        )}
        {collapsed && (
          <>
            {!isAuthorizedToAccess(MODULES.EDO, PERMISSION_FEATURES.VIEW) ? null :
              tip("EDO Request",
                <div className={subItemCls(ROUTES.edo)} onClick={() => go(ROUTES.edo)}>
                  <span className={classes.navIcon}><NodeIndexOutlined /></span>
                </div>
              )
            }
            {!isAuthorizedToAccess(MODULES.EDO, PERMISSION_FEATURES.VIEW) ? null :
              tip("Invoice Request",
                <div className={subItemCls(ROUTES.invoiceRequests)} onClick={() => go(ROUTES.invoiceRequests)}>
                  <span className={classes.navIcon}><FileDoneOutlined /></span>
                </div>
              )
            }
          </>
        )}

        {/* MDO — top-level nav item */}
        {isAuthorizedToAccess(MODULES.MDO, PERMISSION_FEATURES.VIEW) &&
          tip("MDO Management",
            <div className={itemCls(ROUTES.mdo)} onClick={() => go(ROUTES.mdo)}>
              <span className={classes.navIcon}><UnorderedListOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>MDO Management</span>}
            </div>
          )
        }

        {isAuthorizedToAccess(MODULES.USER_MANAGEMENT, PERMISSION_FEATURES.VIEW) &&
          tip("User Management",
            <div className={itemCls(ROUTES.userManagement)} onClick={() => go(ROUTES.userManagement)}>
              <span className={classes.navIcon}><UserAddOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>User Management</span>}
            </div>
          )
        }

        {isAuthorizedToAccess(MODULES.CUSTOMER_MANAGEMENT, PERMISSION_FEATURES.VIEW) &&
          tip("Customer Management",
            <div className={itemCls(ROUTES.customerManagement)} onClick={() => go(ROUTES.customerManagement)}>
              <span className={classes.navIcon}><UsergroupAddOutlined /></span>
              {!collapsed && <span className={classes.navLabel}>Customer Management</span>}
            </div>
          )
        }

        {(isAuthorizedToAccess(MODULES.NOTICE_MANAGEMENT, PERMISSION_FEATURES.VIEW) || isAuthorizedToAccess(MODULES.VESSEL_NOTICE, PERMISSION_FEATURES.VIEW)) && (
          <>
            {tip("Notice Management",
              <div
                className={`${classes.parentItem}${noticeOpen ? ` ${classes.parentOpen}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`}
                onClick={() => setNoticeOpen((v) => !v)}
              >
                <span className={classes.navIcon}><ExclamationCircleOutlined /></span>
                {!collapsed && <span className={classes.navLabel}>Notice Management</span>}
                {!collapsed && <span className={`${classes.chevron}${noticeOpen ? ` ${classes.chevronOpen}` : ""}`}><DownOutlined /></span>}
              </div>
            )}
            <div className={`${classes.subItems}${noticeOpen ? ` ${classes.subItemsOpen}` : ""}`}>
              {isAuthorizedToAccess(MODULES.NOTICE_MANAGEMENT, PERMISSION_FEATURES.VIEW) &&
                <div className={itemCls(ROUTES.noticeManagement)} onClick={() => go(ROUTES.noticeManagement)}>
                  <span className={classes.navIcon}><ExclamationCircleOutlined /></span>
                  {!collapsed && <span className={classes.navLabel}>System Notice</span>}
                </div>
              }
              {isAuthorizedToAccess(MODULES.VESSEL_NOTICE, PERMISSION_FEATURES.VIEW) &&
                <div className={itemCls(ROUTES.vesselNotice)} onClick={() => go(ROUTES.vesselNotice)}>
                  <span className={classes.navIcon}><DeploymentUnitOutlined /></span>
                  {!collapsed && <span className={classes.navLabel}>Vessel Notice</span>}
                </div>
              }
            </div>
          </>
        )}

        {tip("Gensoft Track & Trace",
          <div className={itemCls(ROUTES.gensoftTrackTrace)} onClick={() => go(ROUTES.gensoftTrackTrace)}>
            <span className={classes.navIcon}><NodeIndexOutlined /></span>
            {!collapsed && <span className={classes.navLabel}>Gensoft Track & Trace</span>}
          </div>
        )}

        {(
          isAuthorizedToAccess(MODULES.ACCESS_MANAGEMENT, PERMISSION_FEATURES.VIEW) ||
          isAuthorizedToAccess(MODULES.TERMINAL_MANAGEMENT, PERMISSION_FEATURES.VIEW) ||
          isAuthorizedToAccess(MODULES.SALES_NOMINATION, PERMISSION_FEATURES.VIEW)
        ) && (
          <>
            {tip("Settings",
              <div
                className={`${classes.parentItem}${settingsOpen ? ` ${classes.parentOpen}` : ""}${collapsed ? ` ${classes.navItemCollapsed}` : ""}`}
                onClick={() => setSettingsOpen((v) => !v)}
              >
                <span className={classes.navIcon}><SettingOutlined /></span>
                {!collapsed && <span className={classes.navLabel}>Settings</span>}
                {!collapsed && <span className={`${classes.chevron}${settingsOpen ? ` ${classes.chevronOpen}` : ""}`}><DownOutlined /></span>}
              </div>
            )}
            {!collapsed && (
              <div className={`${classes.subItems}${settingsOpen ? ` ${classes.subItemsOpen}` : ""}`}>
                {isAuthorizedToAccess(MODULES.ACCESS_MANAGEMENT, PERMISSION_FEATURES.VIEW) && (
                  <div className={subItemCls(ROUTES.accessManagement)} onClick={() => go(ROUTES.accessManagement)}>
                    <span className={classes.navIcon}><ApartmentOutlined /></span>
                    Access Management
                  </div>
                )}
                <div className={subItemCls(ROUTES.approvals)} onClick={() => go(ROUTES.approvals)}>
                  <span className={classes.navIcon}><CheckCircleOutlined /></span>
                  Approvals
                </div>
                {isAuthorizedToAccess(MODULES.TERMINAL_MANAGEMENT, PERMISSION_FEATURES.VIEW) && (
                  <div className={subItemCls(ROUTES.terminalManagement)} onClick={() => go(ROUTES.terminalManagement)}>
                    <span className={classes.navIcon}><DeploymentUnitOutlined /></span>
                    Terminal Management
                  </div>
                )}
                {isAuthorizedToAccess(MODULES.SALES_NOMINATION, PERMISSION_FEATURES.VIEW) && (
                  <div className={subItemCls(ROUTES.salesNomination)} onClick={() => go(ROUTES.salesNomination)}>
                    <span className={classes.navIcon}><UserAddOutlined /></span>
                    Sales Nomination
                  </div>
                )}
              </div>
            )}
            {collapsed && (
              <>
                {isAuthorizedToAccess(MODULES.ACCESS_MANAGEMENT, PERMISSION_FEATURES.VIEW) &&
                  tip("Access Management",
                    <div className={subItemCls(ROUTES.accessManagement)} onClick={() => go(ROUTES.accessManagement)}>
                      <span className={classes.navIcon}><ApartmentOutlined /></span>
                    </div>
                  )
                }
                {tip("Approvals",
                  <div className={subItemCls(ROUTES.approvals)} onClick={() => go(ROUTES.approvals)}>
                    <span className={classes.navIcon}><CheckCircleOutlined /></span>
                  </div>
                )}
                {isAuthorizedToAccess(MODULES.TERMINAL_MANAGEMENT, PERMISSION_FEATURES.VIEW) &&
                  tip("Terminal Management",
                    <div className={subItemCls(ROUTES.terminalManagement)} onClick={() => go(ROUTES.terminalManagement)}>
                      <span className={classes.navIcon}><DeploymentUnitOutlined /></span>
                    </div>
                  )
                }
                {isAuthorizedToAccess(MODULES.SALES_NOMINATION, PERMISSION_FEATURES.VIEW) &&
                  tip("Sales Nomination",
                    <div className={subItemCls(ROUTES.salesNomination)} onClick={() => go(ROUTES.salesNomination)}>
                      <span className={classes.navIcon}><UserAddOutlined /></span>
                    </div>
                  )
                }
              </>
            )}
          </>
        )}
      </nav>

    </>
  );
};

export default NavMenu;
